import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Bell,
  CalendarDays,
  Check,
  LayoutDashboard,
  Menu,
  Moon,
  PlayCircle,
  Plus,
  Shield,
  Sparkles,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { buildPublicBookingUrl, resolveLandingOrigin, resolvePanelLoginUrl, resolvePanelRegisterUrl } from '../utils/booking';
import { applySeo } from '../utils/seo';
import '../styles/landing.css';

const trustPoints = [
  'Bezpłatny start bez karty',
  'Konfiguracja w 5 minut',
  'Ponad 3 400 zadowolonych użytkowników',
];

const features = [
  {
    title: 'Rezerwacje online 24/7',
    description: 'Twoi klienci umawiają się sami przez link lub stronę, a Ty dostajesz potwierdzenie bez telefonów i przepisywania terminów.',
    icon: CalendarDays,
  },
  {
    title: 'Zarządzanie klientami',
    description: 'Historia wizyt, notatki, preferencje i dane kontaktowe każdej klientki w jednym miejscu, gotowe do działania na telefonie i komputerze.',
    icon: Users,
  },
  {
    title: 'Automatyczne przypomnienia',
    description: 'SMS i e-mail przed wizytą ograniczają nieobecności i dają klientkom jasny sygnał, kiedy mają się pojawić.',
    icon: Bell,
  },
  {
    title: 'Statystyki i raporty',
    description: 'Widzisz, które usługi sprzedają się najlepiej, kiedy masz największe obłożenie i jak wracają Twoi klienci.',
    items: [
      'Raport tygodniowy i miesięczny',
      'Analiza godzin szczytu',
      'Retencja i powroty klientów',
      'Eksport do CSV i PDF',
      'Śledzenie przychodów',
      'Porównanie okresów',
    ],
    featured: true,
    icon: BarChart3,
  },
];

const industries = [
  ['💇‍♀️', 'Kosmetyczka', 'Zabiegi, manicure, stylizacje'],
  ['✂️', 'Fryzjer / Barber', 'Strzyżenie, koloryzacja, beard'],
  ['🏋️', 'Trener personalny', 'Sesje indywidualne i grupowe'],
  ['🔧', 'Mechanik / Warsztat', 'Przeglądy, naprawy, serwis'],
  ['🧘', 'Psycholog / Terapeuta', 'Sesje, konsultacje, coaching'],
  ['💆', 'Masażysta / SPA', 'Masaże, zabiegi relaksacyjne'],
  ['📊', 'Doradca / Konsultant', 'Finansowy, prawny, IT'],
  ['👗', 'Stylistka / Image', 'Zakupy, styl, wizerunek'],
  ['🐾', 'Weterynarz / Groomer', 'Wizyty, strzyżenie, pielęgnacja'],
];

const testimonials = [
  {
    initials: 'MK',
    author: 'Marta Kowalczyk',
    role: 'Kosmetyczka · Warszawa',
    text: 'ClientWay pozwala mi skupić się na pracy, a nie na kalendarzu. Wcześniej traciłam 2 godziny dziennie na umawianie wizyt przez telefon. Teraz zero chaosu.',
    accent: 'linear-gradient(135deg,#00BFA6,#008F7A)',
    highlight: true,
  },
  {
    initials: 'TN',
    author: 'Tomasz Nowak',
    role: 'Trener personalny · Kraków',
    text: 'Potrzebowałem czegoś prostego. ClientWay ma wszystko: rezerwacje, przypomnienia i porządek w grafiku. Klienci są zachwyceni, że mogą umawiać się o każdej porze.',
    accent: 'linear-gradient(135deg,#3B82F6,#1D4ED8)',
  },
  {
    initials: 'AZ',
    author: 'dr Anna Zając',
    role: 'Psycholog · Wrocław',
    text: 'Prowadzę gabinet psychologiczny i zależy mi na prostocie. Moi pacjenci rezerwują sami, a ja mam spokojną głowę i czysty plan dnia.',
    accent: 'linear-gradient(135deg,#8B5CF6,#6D28D9)',
  },
  {
    initials: 'PW',
    author: 'Piotr Wiśniewski',
    role: 'Właściciel warsztatu · Poznań',
    text: 'W naszym warsztacie ClientWay uporządkował zapisy na przeglądy. Mechanicy widzą plan dnia od rana, klienci dostają przypomnienia SMS. Genialnie proste.',
    accent: 'linear-gradient(135deg,#F59E0B,#D97706)',
  },
  {
    initials: 'JB',
    author: 'Jakub Barański',
    role: 'Barber · Gdańsk',
    text: 'Mój salon przyjmuje teraz więcej klientów bez dokładania chaosu. Rezerwacje działają także wieczorem i w weekend, kiedy akurat nikt nie odbiera telefonu.',
    accent: 'linear-gradient(135deg,#1A2E4F,#2E4170)',
  },
  {
    initials: 'KS',
    author: 'Katarzyna Sikora',
    role: 'Konsultant IT · Łódź',
    text: 'ClientWay eliminuje całą logistykę umawiania spotkań. Klient sam wybiera termin, ja dostaję potwierdzenie i mogę od razu skupić się na pracy.',
    accent: 'linear-gradient(135deg,#EC4899,#BE185D)',
  },
];

const steps = [
  {
    title: 'Utwórz konto',
    description: 'Zarejestruj się, dodaj usługi i godziny pracy. Konfigurator prowadzi Cię krok po kroku.',
  },
  {
    title: 'Udostępnij link',
    description: 'Wyślij klientom swój link do rezerwacji albo wstaw go na stronę. Zaczyna działać od razu.',
  },
  {
    title: 'Zarządzaj spokojnie',
    description: 'Widzisz kalendarz, klientów i raporty w jednym panelu. Bez przepisywania i bez chaosu.',
  },
];

export default function LandingPage() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    return window.localStorage.getItem('clientway-landing-theme') || 'light';
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const panelLoginUrl = resolvePanelLoginUrl();
  const panelRegisterUrl = resolvePanelRegisterUrl();
  const demoBookingUrl = buildPublicBookingUrl('ania');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.localStorage.setItem('clientway-landing-theme', theme);

    const landingOrigin = resolveLandingOrigin();
    applySeo({
      title: 'ClientWay - Rezerwacje online i klienci w jednym miejscu',
      description: 'ClientWay pomaga usługodawcom ogarnąć rezerwacje online, klientów, przypomnienia i grafik w jednym prostym panelu.',
      keywords: 'rezerwacje online, system rezerwacji, klienci, harmonogram, CRM dla usług, salon urody, barber, trener personalny, psycholog, konsultant',
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      canonical: landingOrigin,
      ogUrl: landingOrigin,
      themeColor: theme === 'dark' ? '#0c1422' : '#f8fafb',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ClientWay',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: landingOrigin,
        description: 'ClientWay pomaga usługodawcom zarządzać rezerwacjami online, klientami, przypomnieniami i grafikiem w jednym prostym panelu.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'PLN',
        },
        publisher: {
          '@type': 'Organization',
          name: 'ClientWay',
          url: landingOrigin,
        },
      },
    });

    const rootElement = rootRef.current;
    if (!rootElement) {
      return undefined;
    }

    const revealElements = Array.from(rootElement.querySelectorAll('.landing-reveal'));

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach((element) => element.classList.add('visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    revealElements.forEach((element) => observer.observe(element));

    function handleScroll() {
      setMenuOpen(false);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [theme]);

  function handleSectionLinkClick() {
    setMenuOpen(false);
  }

  function handleCtaClick(label, target) {
    trackEvent('cta_click', {
      cta_label: label,
      cta_target: target,
      cta_section: 'landing',
    });
  }

  return (
    <div className="landing-page" data-theme={theme} ref={rootRef}>
      <nav className="landing-nav" aria-label="Nawigacja główna">
        <a href="/" className="landing-nav-logo" aria-label="ClientWay - strona główna">
          <span className="landing-logo-icon"><LayoutDashboard size={18} /></span>
          <span className="landing-logo-text">Client<span>Way</span></span>
        </a>

        <ul className="landing-nav-links">
          <li><a href="#features">Funkcje</a></li>
          <li><a href="#for-whom">Dla kogo</a></li>
          <li><a href="#how-it-works">Jak działa</a></li>
          <li><a href="#testimonials">Opinie</a></li>
        </ul>

        <div className="landing-nav-actions">
          <button
            type="button"
            className="landing-theme-toggle"
            aria-label="Przełącz motyw"
            onClick={() => setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'))}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a href={panelLoginUrl} className="landing-nav-login" onClick={() => handleCtaClick('nav_login', panelLoginUrl)}>Zaloguj się</a>
          <a href={panelRegisterUrl} className="landing-btn-primary" onClick={() => handleCtaClick('nav_start_free', panelRegisterUrl)}>Zacznij za darmo</a>
          <button
            type="button"
            className="landing-hamburger"
            aria-label="Menu mobilne"
            onClick={() => setMenuOpen((currentState) => !currentState)}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <div className={`landing-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <a href="#features" onClick={handleSectionLinkClick}>Funkcje</a>
        <a href="#for-whom" onClick={handleSectionLinkClick}>Dla kogo</a>
        <a href="#how-it-works" onClick={handleSectionLinkClick}>Jak działa</a>
        <a href="#testimonials" onClick={handleSectionLinkClick}>Opinie</a>
        <a href={panelLoginUrl} onClick={() => { handleSectionLinkClick(); handleCtaClick('mobile_login', panelLoginUrl); }}>Zaloguj się</a>
        <a href={panelRegisterUrl} className="landing-btn-primary landing-mobile-cta" onClick={() => { handleSectionLinkClick(); handleCtaClick('mobile_start_free', panelRegisterUrl); }}>Zacznij za darmo</a>
      </div>

      <section id="hero" className="landing-hero" aria-label="Sekcja główna">
        <div className="landing-hero-grid" aria-hidden="true" />
        <div className="landing-hero-glow" aria-hidden="true" />

        <div className="landing-container landing-hero-content">
          <div className="landing-badge" aria-label="Nowość">
            <Sparkles size={12} />
            Nowe: automatyczne przypomnienia SMS
          </div>

          <h1 className="landing-hero-headline">
            Zarządzaj rezerwacjami
            <br />
            i klientami w jednym miejscu.
          </h1>

          <p className="landing-hero-sub">
            ClientWay to proste narzędzie dla każdego, kto pracuje z klientami: od salonu po studio, od gabinetu po warsztat.
          </p>

          <div className="landing-hero-cta">
            <a href={panelRegisterUrl} className="landing-btn-hero-primary" onClick={() => handleCtaClick('hero_start_free', panelRegisterUrl)}>
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
            <a href={demoBookingUrl} className="landing-btn-hero-secondary" onClick={() => handleCtaClick('hero_booking_demo', demoBookingUrl)}>
              <PlayCircle size={16} />
              Zobacz demo rezerwacji
            </a>
          </div>

          <div className="landing-hero-trust" role="list">
            {trustPoints.map((item) => (
              <div key={item} className="landing-hero-trust-item" role="listitem">
                <Check size={14} />
                {item}
              </div>
            ))}
          </div>

          <div className="landing-hero-visual" aria-hidden="true">
            <div className="landing-dashboard-frame">
              <div className="landing-db-topbar">
                <span className="landing-db-dot landing-db-dot-r" />
                <span className="landing-db-dot landing-db-dot-y" />
                <span className="landing-db-dot landing-db-dot-g" />
                <span className="landing-db-title">ClientWay - Panel główny</span>
              </div>

              <div className="landing-db-body">
                <div className="landing-db-sidebar">
                  <div className="landing-db-sidebar-label">Menu</div>
                  <div className="landing-db-nav-item active">Przegląd</div>
                  <div className="landing-db-nav-item">Rezerwacje</div>
                  <div className="landing-db-nav-item">Klienci</div>
                  <div className="landing-db-nav-item">Raporty</div>
                  <div className="landing-db-nav-item">Przypomnienia</div>
                  <div className="landing-db-nav-footer">
                    <div className="landing-db-nav-item">Ustawienia</div>
                  </div>
                </div>

                <div className="landing-db-main">
                  <div className="landing-db-header">
                    <span className="landing-db-page-title">Dzisiaj, 4 kwietnia 2026</span>
                    <span className="landing-db-add-btn">
                      <Plus size={10} />
                      Nowa rezerwacja
                    </span>
                  </div>

                  <div className="landing-db-stats">
                    <div className="landing-db-stat">
                      <div className="landing-db-stat-label">Dzisiaj</div>
                      <div className="landing-db-stat-value landing-teal">8</div>
                      <div className="landing-db-stat-change">+2 vs wczoraj</div>
                    </div>
                    <div className="landing-db-stat">
                      <div className="landing-db-stat-label">Klienci</div>
                      <div className="landing-db-stat-value">247</div>
                      <div className="landing-db-stat-change">+14 w tygodniu</div>
                    </div>
                    <div className="landing-db-stat">
                      <div className="landing-db-stat-label">Przychód</div>
                      <div className="landing-db-stat-value">4 820 zł</div>
                      <div className="landing-db-stat-change">+18% w mies.</div>
                    </div>
                  </div>

                  <div className="landing-db-reservations">
                    <div className="landing-db-res-title">Nadchodzące rezerwacje</div>
                    {[
                      ['AK', 'Anna Kowalska', '09:30 • Strzyżenie + styling', 'confirmed'],
                      ['MP', 'Marek Piotrowski', '11:00 • Konsultacja', 'pending'],
                      ['EW', 'Ewa Wiśniewska', '14:15 • Sesja treningowa', 'new'],
                    ].map(([initials, name, time, tone]) => (
                      <div key={name} className="landing-db-res-item">
                        <div className={`landing-db-avatar landing-${tone}`}>{initials}</div>
                        <div className="landing-db-res-info">
                          <div className="landing-db-res-name">{name}</div>
                          <div className="landing-db-res-time">{time}</div>
                        </div>
                        <span className={`landing-db-tag ${tone}`}>{tone === 'confirmed' ? 'Potwierdzone' : tone === 'pending' ? 'Oczekuje' : 'Nowa'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="landing-phone-frame">
              <div className="landing-phone-bar"><span className="landing-phone-notch" /></div>
              <div className="landing-phone-body">
                <div className="landing-phone-header">Twoje rezerwacje</div>
                <div className="landing-phone-booking">
                  <strong>Strzyżenie + styling</strong>
                  <span>Sob, 4 kwi • 09:30</span>
                  <em>Potwierdzone</em>
                </div>
                <div className="landing-phone-booking reminder">
                  <strong>Konsultacja biznesowa</strong>
                  <span>Sob, 4 kwi • 11:00</span>
                  <em>Przypomnienie 1h</em>
                </div>
                <div className="landing-phone-button">+ Nowa rezerwacja</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="landing-section" aria-labelledby="features-heading">
        <div className="landing-container">
          <div className="landing-section-header landing-reveal">
            <p className="landing-section-label">Funkcje</p>
            <h2 id="features-heading">Wszystko, czego potrzebujesz do profesjonalnej obsługi klientów</h2>
            <p>Cztery kluczowe moduły, które razem tworzą kompletny system zarządzania Twoim biznesem.</p>
          </div>

          <div className="landing-features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className={`landing-feature-card ${feature.featured ? 'featured' : ''} landing-reveal landing-reveal-delay-${Math.min(index + 1, 4)}`}
                >
                  <div className="landing-feature-icon"><Icon size={22} /></div>
                  <h3>{feature.title}</h3>
                  <p className="landing-feature-desc">{feature.description}</p>
                  {feature.items && (
                    <ul className="landing-feature-list">
                      {feature.items.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="for-whom" className="landing-section landing-section-muted" aria-labelledby="forwho-heading">
        <div className="landing-container">
          <div className="landing-section-header landing-reveal">
            <p className="landing-section-label">Dla kogo</p>
            <h2 id="forwho-heading">ClientWay działa w każdej branży usługowej</h2>
            <p>Jeśli pracujesz z klientami i zarządzasz swoim czasem, ClientWay jest dla Ciebie.</p>
          </div>

          <div className="landing-industries-grid">
            {industries.map(([emoji, name, description], index) => (
              <article key={name} className={`landing-industry-card landing-reveal landing-reveal-delay-${(index % 4) + 1}`}>
                <span className="landing-industry-emoji">{emoji}</span>
                <div className="landing-industry-name">{name}</div>
                <div className="landing-industry-desc">{description}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="landing-section landing-section-muted" aria-labelledby="how-heading">
        <div className="landing-container">
          <div className="landing-section-header landing-reveal">
            <p className="landing-section-label">Jak działa</p>
            <h2 id="how-heading">Gotowe w 3 prostych krokach</h2>
            <p>Nie potrzebujesz żadnej wiedzy technicznej. Zaczynasz w kilka minut.</p>
          </div>

          <div className="landing-steps-grid">
            {steps.map((step, index) => (
              <article key={step.title} className={`landing-step-card landing-reveal landing-reveal-delay-${index + 1}`}>
                <div className="landing-step-number">{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="landing-section" aria-labelledby="testimonials-heading">
        <div className="landing-container">
          <div className="landing-section-header landing-reveal">
            <p className="landing-section-label">Opinie</p>
            <h2 id="testimonials-heading">Zaufali nam usługodawcy z całej Polski</h2>
            <p>Ponad 3 400 specjalistów zarządza swoim biznesem przez ClientWay każdego dnia.</p>
          </div>

          <div className="landing-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <article
                key={testimonial.author}
                className={`landing-testimonial-card ${testimonial.highlight ? 'highlight' : ''} landing-reveal landing-reveal-delay-${(index % 3) + 1}`}
              >
                <div className="landing-stars">★★★★★</div>
                <span className="landing-quote-mark">&quot;</span>
                <p className="landing-testimonial-text">{testimonial.text}</p>
                <div className="landing-testimonial-author">
                  <div className="landing-testimonial-avatar" style={{ background: testimonial.accent }}>{testimonial.initials}</div>
                  <div>
                    <div className="landing-testimonial-name">{testimonial.author}</div>
                    <div className="landing-testimonial-role">{testimonial.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="final-cta" className="landing-final-cta" aria-labelledby="cta-heading">
        <div className="landing-container landing-cta-inner">
          <div className="landing-badge landing-badge-cta">
            <Shield size={12} />
            Bezpieczne · Bez karty · Bez zobowiązań
          </div>
          <h2 id="cta-heading">Zacznij zarządzać swoim czasem jak profesjonalista.</h2>
          <p>Dołącz do specjalistów, którzy oszczędzają czas i rozwijają swój biznes z ClientWay.</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary" onClick={() => handleCtaClick('final_start_free', panelRegisterUrl)}>
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
            <a href={demoBookingUrl} className="landing-btn-cta-secondary" onClick={() => handleCtaClick('final_booking_demo', demoBookingUrl)}>
              Zobacz demo rezerwacji
            </a>
          </div>
          <p className="landing-cta-note">14 dni bezpłatnie · Pełne funkcje · Anuluj kiedy chcesz</p>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <a href="/" className="landing-nav-logo">
              <span className="landing-logo-icon"><LayoutDashboard size={18} /></span>
              <span className="landing-logo-text">Client<span>Way</span></span>
            </a>
            <p className="landing-footer-tagline">Twój sposób na lepsze zarządzanie klientami.</p>
          </div>

          <div className="landing-footer-links">
            <div className="landing-footer-col">
              <h4>Produkt</h4>
              <ul>
                <li><a href="#features">Funkcje</a></li>
                <li><a href="#final-cta">Start</a></li>
                <li><a href="#how-it-works">Jak działa</a></li>
                <li><a href="#testimonials">Opinie</a></li>
                <li><a href="/dlaczego-za-darmo">Dlaczego za darmo</a></li>
              </ul>
            </div>
            <div className="landing-footer-col">
              <h4>Dla branż</h4>
              <ul>
                <li><a href="/fryzjer">Fryzjer</a></li>
                <li><a href="/kosmetyczka">Kosmetyczka</a></li>
                <li><a href="/barber">Barber</a></li>
                <li><a href="/fizjoterapeuta">Fizjoterapeuta</a></li>
                <li><a href="/trener-personalny">Trener personalny</a></li>
              </ul>
            </div>
            <div className="landing-footer-col">
              <h4>Wejście</h4>
              <ul>
                <li><a href={panelLoginUrl} onClick={() => handleCtaClick('footer_login', panelLoginUrl)}>Zaloguj się</a></li>
                <li><a href={panelRegisterUrl} onClick={() => handleCtaClick('footer_signup', panelRegisterUrl)}>Załóż konto</a></li>
                <li><a href="#final-cta" onClick={() => handleCtaClick('footer_free_start_anchor', '#final-cta')}>Bezpłatny start</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="landing-footer-bottom">
          <p className="landing-footer-copy">© 2026 ClientWay. Wszelkie prawa zastrzeżone.</p>
          <div className="landing-footer-social" aria-label="Media społecznościowe">
            <a href="#final-cta" className="landing-social-btn" aria-label="Instagram">IG</a>
            <a href="#final-cta" className="landing-social-btn" aria-label="LinkedIn">in</a>
            <a href="#final-cta" className="landing-social-btn" aria-label="Facebook">f</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
